import React from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, ArrowDownRight, Clock } from "lucide-react";

export default function ActivityList({ transactions = [] }) {
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("id-ID", {
      style: "currency",
      currency: "IDR",
      minimumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (date) => {
    const d = new Date(date);
    const now = new Date();
    const diffMs = now - d;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 60) return `${diffMins} minutes ago`;
    if (diffHours < 24) return `${diffHours} hours ago`;
    if (diffDays < 7) return `${diffDays} days ago`;
    return d.toLocaleDateString("id-ID", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const recentTrx = transactions.slice(-6).reverse();

  return (
    <div
      style={{
        background: "#ffffff",
        border: "1px solid #e2e8f0",
        borderRadius: "12px",
        padding: "20px",
        boxShadow:
          "0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px -1px rgba(0, 0, 0, 0.1)",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: "20px",
        }}
      >
        <h3
          style={{
            fontSize: "16px",
            fontWeight: "600",
            color: "#0f172a",
            margin: 0,
          }}
        >
          Recent Transactions
        </h3>
        <Clock size={18} color="#94a3b8" />
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: "1px" }}>
        {recentTrx.length === 0 ? (
          <div
            style={{
              textAlign: "center",
              padding: "40px 20px",
              color: "#94a3b8",
            }}
          >
            <p style={{ margin: 0, fontSize: "14px" }}>No transactions yet</p>
          </div>
        ) : (
          recentTrx.map((trx, idx) => {
            const isIncome = trx.type === "income" || trx.type === "pemasukkan";

            return (
              <motion.div
                key={trx.transaction_id || trx.id || idx}
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.3, delay: idx * 0.05 }}
                whileHover={{
                  backgroundColor: "#f8fafc",
                  boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
                  transition: { duration: 0.2 },
                }}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  padding: "12px 0",
                  borderBottom:
                    idx < recentTrx.length - 1 ? "1px solid #f1f5f9" : "none",
                  transition: "all 0.2s ease",
                  borderRadius: "8px",
                  paddingLeft: "8px",
                  paddingRight: "8px",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "12px",
                    flex: 1,
                  }}
                >
                  <div
                    style={{
                      width: "36px",
                      height: "36px",
                      borderRadius: "8px",
                      background: isIncome ? "#f0fdf4" : "#fef2f2",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    {isIncome ? (
                      <ArrowUpRight
                        size={18}
                        color="#22c55e"
                        strokeWidth={2.5}
                      />
                    ) : (
                      <ArrowDownRight
                        size={18}
                        color="#ef4444"
                        strokeWidth={2.5}
                      />
                    )}
                  </div>

                  <div style={{ flex: 1 }}>
                    <p
                      style={{
                        margin: 0,
                        fontSize: "14px",
                        fontWeight: "500",
                        color: "#0f172a",
                        marginBottom: "2px",
                      }}
                    >
                      {trx.description || "Transaction"}
                    </p>
                    <p
                      style={{
                        margin: 0,
                        fontSize: "12px",
                        color: "#94a3b8",
                      }}
                    >
                      {formatDate(trx.date || trx.created_at || new Date())}
                    </p>
                  </div>
                </div>

                <div style={{ textAlign: "right" }}>
                  <p
                    style={{
                      margin: 0,
                      fontSize: "15px",
                      fontWeight: "600",
                      color: isIncome ? "#22c55e" : "#ef4444",
                    }}
                  >
                    {isIncome ? "+" : "-"} {formatCurrency(trx.amount)}
                  </p>
                </div>
              </motion.div>
            );
          })
        )}
      </div>

      {recentTrx.length > 0 && (
        <button
          style={{
            width: "100%",
            marginTop: "16px",
            padding: "10px",
            background: "transparent",
            border: "1px solid #e2e8f0",
            borderRadius: "8px",
            color: "#64748b",
            fontSize: "13px",
            fontWeight: "500",
            cursor: "pointer",
            transition: "all 0.2s",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = "#f8fafc";
            e.currentTarget.style.borderColor = "#cbd5e1";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = "transparent";
            e.currentTarget.style.borderColor = "#e2e8f0";
          }}
        >
          View All Transactions
        </button>
      )}
    </div>
  );
}
